import { useEffect, useState } from "react";


// Models
import type ICarouselContentDTO from "../../../../Models/DTOs/ICarouselContentDTO";
import axios from "axios";
import {GetMixedContent} from "../../../../Api/MixedContent";
import {MixedContentItemDTO} from "../../../../Models/DTOs/CountWrapper";
import {transformMixedItemToCarouselContent} from "../../../../Helpers/DataConversion/ConvertToCarouselContentItem"; // adjust path

function useFetchCategoryMixedContent(
    categoryId: number | null,
    take: number,
    skip: number,
    shouldAppendData: boolean,
    order: string = "desc",
    publishedOnly: boolean = true
) {
    const [content, setContent] = useState<ICarouselContentDTO[]>([]);
    const [count, setCount] = useState<number>(0);

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");

    useEffect(() => {
        let isCancelled = false;
        const abortController = new AbortController();

        if (categoryId !== null) {
            setLoading(true);
            setError("");

            (async () => {
                try {
                    const data = await GetMixedContent({
                        categoryId,
                        take,
                        skip,
                        order,
                        publishedOnly,
                        signal: abortController.signal,
                    });

                    if (isCancelled) return;

                    const items: MixedContentItemDTO[] = data.items ?? [];

                    // Transform + drop nulls
                    const transformed = items
                        .map(transformMixedItemToCarouselContent)
                        .filter((x): x is ICarouselContentDTO => x !== null);

                    // Optional: dedupe across series/videos by Id.
                    // WARNING: if a Series Id can collide with a Video Id, this will incorrectly drop items.
                    // If that’s possible, you should dedupe by `${ContentType}-${Id}` instead.
                    const deduped = transformed.filter(
                        (item, index, self) =>
                            index === self.findIndex((t) => t.Id === item.Id)
                    );

                    if (shouldAppendData) {
                        setContent((prev) => [...prev, ...deduped]);
                    } else {
                        setContent(deduped);
                    }

                    setCount(data.count ?? deduped.length);
                } catch (e) {
                    if (isCancelled) return;

                    // Abort is normal during unmount / rapid changes
                    if ((e as any)?.name === "CanceledError") return;
                    if (axios.isCancel?.(e)) return;

                    console.error("GetMixedContent error:", e);
                    setError("Failed to fetch category content.");
                } finally {
                    if (!isCancelled) setLoading(false);
                }
            })();
        }

        return () => {
            isCancelled = true;
            abortController.abort();
        };
    }, [categoryId, take, skip, shouldAppendData, order, publishedOnly]);

    return { content, count, loading, error };
}

export default useFetchCategoryMixedContent;
